import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ym from 'react-yandex-metrika';

import { useDispatch, useSelector } from 'react-redux';
import { IntlShape, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useStaticUrls, MarkdownExtXContent as Markdown } from 'platform-components';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { IntroBlock } from '@edu-frontend/modules/src/features/introBlock';
import { Text } from '@edu-frontend/modules/src/components/ui/Text';
import { type BannerCardProps, BannerCardList } from '@edu-frontend/modules/src/entities/user';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { LessonViewWithAddSchoolControl as LessonView } from 'common.components/lesson-view.index/lesson-view_with-add-school-control';
import { getUserId } from 'platform-components/src/components/user/selectors';
import { redirectToAuth } from 'utils/redirect-to-auth';

import {
    SelectorSubjects,
    SubjectGradeSelector,
} from 'common.components/subject-grade-selector/subject-grade-selector';
import qs from 'query-string';
import { DEFAULT_GRADE_INFORMATICS } from 'common.components/user/user.constants';
import Spin from 'platform-components/src/components/spin/spin';
import { lessonModalClose } from '../../library.store/actions';

import './main__public-content.scss';

type BannerItemTextKeys =
    | 'buttonText'
    | 'buttonView'
    | 'description'
    | 'header'
    | 'illustrationBgColor'
    | 'imgSrc'
    | 'backgroundColor';
type BannerItemNames = 'kpk' | 'addClass' | 'teacherReserve';

const getBannerItem = (name: BannerItemNames, onClick: () => void, intl: IntlShape) => {
    const getMessage = (textKey: BannerItemTextKeys) =>
        intl.formatMessage({ id: `publicShowcase.banners.${name}.${textKey}` });

    return {
        buttonText: getMessage('buttonText'),
        backgroundColor: getMessage('backgroundColor') as BannerCardProps['backgroundColor'],
        buttonView: getMessage('buttonView') as BannerCardProps['buttonView'],
        description: getMessage('description'),
        fullWidth: true,
        header: getMessage('header'),
        illustrationBgColor: getMessage('illustrationBgColor') as BannerCardProps['illustrationBgColor'],
        imgSrc: getMessage('imgSrc'),
        onClick,
    };
};

/** Статичная публичная витрина учителя */
export const PublicContent = ({ widgets, isLoading }: { widgets: React.ReactNode; isLoading: boolean }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const staticUrls = useStaticUrls();
    const intl = useIntl();
    const { search } = useLocation();
    const query = qs.parse(search);
    const { grade } = query;
    const userId = useSelector(getUserId);

    const [selectedTab, setSelectedTab] = useState<SelectorSubjects>('informatics');

    // обнуляем таб при переходе на публичную витрину без grade в квери параметрах
    useEffect(() => {
        const isCurrentTabNotInformatics = selectedTab !== 'informatics';
        const withoutGrade = !grade;

        if (isCurrentTabNotInformatics && withoutGrade) {
            setSelectedTab('informatics');
        }
    }, [selectedTab, grade]);

    const redirectOnLabClass = useCallback(() => {
        history.push(staticUrls.labClasses);
    }, [history, staticUrls]);

    const redirectNotAuthedUser = useCallback(() => {
        if (window) {
            redirectToAuth(window.location.href, window.location.href);
        }
    }, []);

    const handleAddClassClick = useCallback(() => {
        ym('reachGoal', 'teacher_main_add_class', { from: 'add_your_class' });
        redirectOnLabClass();
    }, [redirectOnLabClass]);

    const handleKPKClick = useCallback(() => {
        ym('reachGoal', 'teacher_main_add_class', { from: 'teacher_training_course' });
        redirectOnLabClass();
    }, [redirectOnLabClass]);

    const handleTeacherReserveClick = useCallback(() => {
        ym('reachGoal', 'teacher_main_add_class', { from: 'hr_reserve' });
        redirectOnLabClass();
    }, [redirectOnLabClass]);

    const { addClassItems, evolveItems } = useMemo(() => {
        return {
            addClassItems: [getBannerItem('addClass', handleAddClassClick, intl)],
            evolveItems: [
                getBannerItem('kpk', handleKPKClick, intl),
                getBannerItem('teacherReserve', handleTeacherReserveClick, intl),
            ],
        };
    }, [handleAddClassClick, handleTeacherReserveClick, handleKPKClick, intl]);

    const handleHeaderActionClick = useCallback(() => {
        if (userId) {
            ym('reachGoal', 'teacher_click_button1_create_class');
            redirectOnLabClass();
        } else {
            ym('reachGoal', 'teacher_click_button1_login');
            redirectNotAuthedUser();
        }
    }, [redirectOnLabClass, redirectNotAuthedUser, userId]);

    const handleFooterActionClick = useCallback(() => {
        if (userId) {
            ym('reachGoal', 'teacher_click_button2_create_class');
            redirectOnLabClass();
        } else {
            ym('reachGoal', 'teacher_click_button2_login');
            redirectNotAuthedUser();
        }
    }, [redirectOnLabClass, redirectNotAuthedUser, userId]);

    return (
        <ThemeProvider isRoot={false} theme={hrLight}>
            <div className="library__container">
                <IntroBlock
                    onFooterActionClick={handleFooterActionClick}
                    onHeaderActionClick={handleHeaderActionClick}
                    step={userId ? 'authed' : 'default'}
                />
                <div className="library">
                    <div className="public-content__container">
                        <div>
                            <Text as="div" className="public-content__page-title" typography="titleL">
                                {intl.formatMessage({ id: 'publicShowcase.title' })}
                            </Text>
                            <SubjectGradeSelector
                                defaultGrade={DEFAULT_GRADE_INFORMATICS}
                                onSubjectSelect={setSelectedTab}
                            />
                            {isLoading && (
                                <div className="public-content__spin">
                                    <Spin />
                                </div>
                            )}
                            {widgets}
                        </div>
                        <div>
                            <Text as="div" className="public-content__banner__title" typography="titleM">
                                {intl.formatMessage({ id: 'publicShowcase.addClass.header' })}
                            </Text>
                            <Text as="div" className="public-content__banner__description-block" typography="bodyM">
                                <Markdown>{intl.formatMessage({ id: 'publicShowcase.addClass.description' })}</Markdown>
                            </Text>
                            <BannerCardList className="public-content__banners" items={addClassItems} />
                        </div>
                        <div>
                            <div className="public-content__evolve__text">
                                <Text as="div" className="public-content__banner__title" typography="titleL">
                                    {intl.formatMessage({ id: 'publicShowcase.evolve.header' })}
                                </Text>
                                <Text as="div" className="public-content__banner__description-block" typography="bodyM">
                                    <Markdown>
                                        {intl.formatMessage({ id: 'publicShowcase.evolve.description' })}
                                    </Markdown>
                                </Text>
                            </div>
                            <BannerCardList className="public-content__banners" items={evolveItems} />
                        </div>
                    </div>
                </div>
            </div>
            <LessonView
                isCopyingEnabled={true}
                onClose={() => dispatch(lessonModalClose())}
                shouldCloseOnGoBack={true}
            />
        </ThemeProvider>
    );
};
